/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './FunctionEditor';

const FunctionEditorManager: ComponentManagerFn<'FunctionEditor'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'FunctionEditor'>(platform, 'FunctionEditor'),
    type: 'FunctionEditor',
    name: 'Function Editor',
    isChildAllowed: (page, item, childType) =>
      ([] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      functionUri: {
        label: 'Function URI',
        type: 'expression',
        returnType: async () => 'string',
      },
      defaultFunction: {
        label: 'Default Function',
        type: 'typescript',
        compileOnChange: true,
        defaultScript: `function myFunction() {
  return 'Hello World!';
}

export { myFunction };`,
      },
    },
    getDisplayText: (item) =>
      item.props.functionUri?.value ?? 'Choose a Function!',
    category: 'Forms',
    icon: { iconName: 'code' },
    description: `**Overview**<br/>
The Function Editor is a specialized code editing component designed specifically for writing and maintaining TypeScript business logic. Unlike the generic **Code** component, this editor is optimized for executable functions, offering features like real-time compilation and syntax validation.
Use this component to build interfaces where developers or power users need to define complex algorithms, server-side logic, or data transformation scripts directly within the application.

**1. Function Identification**<br/>
To manage the code lifecycle, the editor must be linked to a specific resource identifier within your system.
* **Function URI:** An expression that resolves to the unique identifier (URI) of the function being edited. This tells the system which logic block to load and where to save the changes.
* **Type:** Expression (returns String).
* **Example:** \`'urn:cloudio:func:calculateTax'\` or \`$.selectedItem.functionPath\`.

**2. Code Authoring**<br/>
The core of the component is the TypeScript editor interface.
* **Default Function:** Defines the initial template or boilerplate code presented to the user when creating a new function.
* **Standard Pattern:** The editor expects a module pattern. You typically define a function and explicitly export it.
* **Example Template:** \`function myFunction() { return 'Hello World!'; } export { myFunction };\`
* **Compilation:** The component is configured with **compileOnChange: true**. This means as the user types, the editor actively checks for TypeScript syntax errors and compilation issues, providing immediate feedback (red squigglies) just like a desktop IDE.

**3. Best Use Cases**<br/>
* **Low-Code Logic Implementation:** Allowing "Citizen Developers" to extend application functionality by writing small snippets of TypeScript for custom validation or calculation.
* **Serverless Function Management:** Building an admin panel where developers can update backend serverless handlers without redeploying the entire application.
* **ETL Scripting:** Providing an interface to write data transformation rules (Extract, Transform, Load) that process incoming data streams.
* **Dynamic Rules Engines:** Creating complex business rules that go beyond simple "If/Then" logic builders.`,
  };
};

export default FunctionEditorManager;
